<template>
  <div class="nx-file-selector">
    <v-autocomplete
      menu-props="closeOnContentClick"
      :disabled="disabled"
      :items="formattedItems"
      :loading="loading"
      :value="selectedFileId"
      @blur="onBlur"
      @focus="$emit('focus')"
      @input="acceptSelection"
      @update:searchInput="searchByText"
      append-icon="search"
      @click:append="openSearcher"
      chips
      item-path="title"
      item-text="title"
      item-value="id"
      ref="selector"
      v-bind="$attrs"
      :error="hasError"
      :error-messages="errorMessages"
    >

      <template slot="selection" slot-scope="{ item, selected }">
        <v-chip
          :selected="selected"
          :text="truncateTitle(item.title)"
          :title="$t('message.common.delete')"
          @input="remove(item)"
          :close="!readonly"
          class="nx-chip">
          <v-avatar>
            <v-icon class="nx-chip-icon">{{ item.icon }}</v-icon>
          </v-avatar>
          <span>{{ truncateTitle(item.title) }}</span>
          <template v-if="allowEditItems">
            <span v-if="openModalEditor"
                  class="v-chip__close nx-chip--edit"
                  @click.capture.stop="openModalEditorDialog = true">
              <v-icon class="ml-2">edit</v-icon>
            </span>
            <router-link v-else class="nx-chip--edit"
                         :to="{ name: 'entity.editor', params: { id: item.id, entity: 'binaryFile' }}"
                         target="_blank">
              <span  class="v-chip__close nx-chip--edit">
                <v-icon class="ml-2">edit</v-icon>
              </span>
            </router-link>
          </template>
        </v-chip>

      </template>

      <template slot="item" slot-scope="{ item }">
        <v-list-tile-avatar>
          <v-img v-if="item.image" contain :src="item.image"/>
          <v-icon v-else>{{ item.icon }}</v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title v-html="$options.filters.highlight(item.title, searchText)" />
        </v-list-tile-content>
      </template>

      <template slot="no-data">
        <li>
          <v-list-tile>
            <v-list-tile-avatar>
              <v-progress-circular indeterminate v-if="loading"></v-progress-circular>
              <v-icon v-else-if="searchText">not_interested</v-icon>
              <v-icon v-else>edit</v-icon>
            </v-list-tile-avatar>

            <v-list-tile-content>
              <v-list-tile-title v-if="loading">{{ $t('message.common.loading') }}</v-list-tile-title>
              <v-list-tile-title v-else-if="searchText">{{ $t('message.search.noDataFound') }}
              </v-list-tile-title>
              <v-list-tile-title v-else>{{ $t('message.search.typeToSearch') }}</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </li>
      </template>
    </v-autocomplete>

    <nx-searcher-dialog
      v-if="allowSearcher && searcherModal"
      :active="searcherModalTimeout"
      :entity="entity"
      :entityTabs="false"
      :filters="filters"
      :limit="1"
      :text="searchText"
      :value="searcherValue"
      @dismiss="openSearcher(false)"
      @input="acceptSearcherValue"
      :title="$t('message.searcher.name')"
    />

    <nx-dialog-confirm
      :title="$t('message.file.selector.confirmTitle')"
      :text="$t('message.file.selector.confirmText')"
      ref="confirm"
    ></nx-dialog-confirm>

    <nx-entity-modal-editor-dialog
      v-if="openModalEditor"
      :active="openModalEditorDialog"
      :entity="selectedFile"
      type="binaryFile"
      :parent-title="getEntityModalTitle()"
      v-bind="selectedFile"
      @dismiss="dismissModalEditor" />

  </div>
</template>


<script>
import Vue from 'vue';
import isArray from 'lodash/isArray';
import Loadable from '@/mixins/loadable';
import Searchable from '@/modules/searcher/mixins/searchable';
import { isObject, isNumber, isString } from 'lodash';
import { toArray } from '@/utils';
import NxDialogConfirm from '@/components/dialogs/Confirm';
import NxSearcherDialog from '@/modules/searcher/SearcherDialog';
import api from '../api';
import conf from '../config';

/**
 * File selector module.
 *
 * @property {string} [identifier] String to use as identifier for this module.
 * @property {[]} [value] Array of initial selected files.
 */
export default {
  name: 'nx-selector',
  mixins: [Searchable, Loadable],
  components: {
    NxSearcherDialog,
    NxDialogConfirm,
    NxEntityModalEditorDialog: () => import('@/modules/entity/editor/EntityModalEditorDialog'),
  },
  props: {
    allowEditItems: {
      type: Boolean,
      default: true,
    },
    openModalEditor: {
      type: Boolean,
      default: true,
    },
    readonly: {
      type: Boolean,
      default: () => false,
    },
    allowSearcher: {
      type: Boolean,
      default: false,
    },
    collectionId: {
      type: Number,
    },
    additionalFilters: {
      type: Object,
    },
    value: {
      type: [Object, Number, String],
    },
    disabled: {
      type: Boolean,
    },
    titleLength: {
      type: [Number],
      default: 0,
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    entity: 'binaryFile',
    filters: {},
    selectedFile: null,
    searcherModalTimeout: false,
    searcherModal: false,
    openModalEditorDialog: false,
  }),
  computed: {
    hasError() {
      return this.errorMessages.length > 0;
    },
    selectedFileId() {
      if (isObject(this.selectedFile)) return this.selectedFile.id;
      return null;
    },
    formattedItems() {
      const items = this.items
        .map(item => Object.assign({}, item.entity))
        .map(this.formatSelectorItem);
      if (this.selectedFile) {
        items.push(this.formatSelectorItem(this.selectedFile));
      }
      return items;
    },
    searcherValue() {
      return toArray(this.selectedFile);
    },
  },
  methods: {
    acceptSearcherValue(value) {
      if (isArray(value)) {
        console.debug('accept searcher value', value[0]);
        this.$emit('input', value[0]);
      } else {
        console.debug('accept searcher value', value);
        this.$emit('input', value);
      }
    },
    acceptSelection(selectedItem) {
      const selected = this.items.find(item => item.entityId === selectedItem);
      console.debug('accept selection ', selected ? selected.entity : null);
      this.selectedFile = selected ? selected.entity : null;
      this.$emit('input', this.selectedFile);
    },
    openSearcher(value = true) {
      if (!this.allowSearcher) {
        return;
      }
      value = !!value;
      this.searcherModal = value;
      setTimeout(() => {
        this.searcherModalTimeout = value;
      }, 300);
    },
    onBlur() {
      this.$emit('blur', this.selectedFile);
    },
    async remove() {
      const confirm = await this.$refs.confirm.open();
      if (confirm) {
        this.selectedFile = null;
        this.$emit('blur', null);
        this.$emit('input', null);
      }
    },
    /**
     * Maps a file to a FileSearchItem.
     * @param {FileCard} file
     * @returns {FileSelectorItem}
     */
    formatSelectorItem(file) {
      /**
       * @typedef {Object} FileSelectorItem
       * @property {number} id
       * @property {string} icon
       * @property {string} title
       */
      const isImage = file.mimeType && file.mimeType.startsWith('image');
      return {
        id: file.id,
        title: file.name,
        icon: conf.iconFromExtension(file.extension),
        image: isImage ? file.publicUrl || null : null,
      };
    },
    truncateTitle(text) {
      let titleLength = this.titleLength;

      // Set titleLength if not specified
      if (!titleLength && this.$refs.selector) {
        titleLength = (this.$refs.selector.$el.clientWidth * 30) / 375;
      }

      if (text && titleLength > 0) {
        return text.substr(0, titleLength) + ((text.length > titleLength) ? '...' : '');
      }
      return text;
    },
    /**
     * Fetch the a file
     * @param {Number|FileCard|null} file
     * @returns {FileCard}
     */
    async fetchSelectedFile(file) {
      // If the file is an id, fetch it
      if (!file) {
        return null;
      }
      if (isNumber(file) || isString(file)) {
        return api.fetch(file);
      }
      return file;
    },
    getEntityModalTitle() {
      if (!this.$getParentEditor || !this.openModalEditor) {
        return null;
      }
      return this.$getParentEditor().description || this.$getParentEditor().title;
    },
    dismissModalEditor() {
      this.openModalEditorDialog = false;
      this.$emit('input', this.selectedFile);
    },
  },
  watch: {
    value: {
      async handler(value) {
        this.loadingStart();
        this.selectedFile = await this.fetchSelectedFile(value);
        this.loadingFinish();
      },
      immediate: true,
    },
    searchText(value) {
      if (value) {
        Vue.set(this.filters, 'text', value);
      } else {
        Vue.delete(this.filters, 'text');
      }
    },
  },
  created() {
    if (this.additionalFilters) {
      this.filters = { ...this.filters, ...this.additionalFilters };
    }
  },
};
</script>

<style scoped lang="stylus">
  .nx-chip--edit {
    pointer-events: all;
    text-decoration: none;
    color: inherit;
    display: contents;
  }
</style>
